/**
 * Idle Session Timeout Configuration
 *
 * HIPAA §164.312(a)(2)(iii) (automatic logoff) requires terminating an
 * electronic session after a predetermined period of inactivity. The
 * 15-minute default follows the common healthcare convention for PHI
 * workstations; deployments can tune it via VITE_-prefixed env vars.
 *
 * - VITE_IDLE_TIMEOUT_ENABLED: set to 'false' to disable (default: enabled)
 * - VITE_IDLE_TIMEOUT_MINUTES: total inactivity before sign-out (default: 15)
 * - VITE_IDLE_WARNING_SECONDS: how long the "stay signed in" warning shows
 *   before sign-out (default: 60; always clamped below the total timeout)
 */

export interface IdleTimeoutConfig {
  enabled: boolean;
  timeoutMs: number;
  warningMs: number;
}

export const DEFAULT_IDLE_TIMEOUT_MINUTES = 15;
export const DEFAULT_IDLE_WARNING_SECONDS = 60;

function parsePositiveNumber(raw: string | undefined, fallback: number): number {
  if (raw === undefined || raw.trim() === '') return fallback;
  const parsed = Number(raw);
  if (!Number.isFinite(parsed) || parsed <= 0) return fallback;
  return parsed;
}

export function parseIdleTimeoutConfig(env: Record<string, string | undefined>): IdleTimeoutConfig {
  const enabled = env.VITE_IDLE_TIMEOUT_ENABLED !== 'false';
  const timeoutMinutes = parsePositiveNumber(
    env.VITE_IDLE_TIMEOUT_MINUTES,
    DEFAULT_IDLE_TIMEOUT_MINUTES,
  );
  const warningSeconds = parsePositiveNumber(
    env.VITE_IDLE_WARNING_SECONDS,
    DEFAULT_IDLE_WARNING_SECONDS,
  );

  const timeoutMs = Math.round(timeoutMinutes * 60_000);
  // The warning must fire strictly before the timeout to be useful.
  const warningMs = Math.min(Math.round(warningSeconds * 1000), Math.max(timeoutMs - 1000, 1000));

  return { enabled, timeoutMs, warningMs };
}
